import React, { useState, useEffect, useRef } from 'react';
import MMAllegroSetupBanner from './MMAllegroSetupBanner';
import { useShortcuts } from './useShortcuts';
import ShortcutSuggestions from './ShortcutSuggestions';
import LiveAlertBar from './LiveAlertBar';
import axios from 'axios';

const API = '/api';

interface Rating {
  id: string;
  createdAt: string;
  recommended: boolean;
  buyer: { id: string; login: string; };
  comment: string;
  rates: { delivery: number; service: number; description: number; deliveryCost: number; };
  order: { id: string; offers: { id: string; title: string; }[]; };
  answer?: { message: string; createdAt: string; };
  removal?: { possibleTo: string; };
  account_id: string;
  account_name: string;
}

export default function Ratings({ onBack, hideHeader, expiredAccounts = [], currentUser = null, onOpenAiOpen = () => {}, onOpenReturns = () => {} }: { onBack: () => void; hideHeader?: boolean; expiredAccounts?: string[]; currentUser?: any; onOpenAiOpen?: () => void; onOpenReturns?: () => void }) {
  const isAccountExpired = (accountName: string) => expiredAccounts.includes(accountName);
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [selected, setSelected] = useState<Rating | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all'|'positive'|'negative'|'unanswered'>('negative');
  const [filterAccount, setFilterAccount] = useState('all');
  const [accounts, setAccounts] = useState<{id:string,name:string}[]>([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [allegroBannerClosed, setAllegroBannerClosed] = React.useState(false);
  // Powrot z OAuth: odczyt znacznika RAZ przy montowaniu (potem baner go skonsumuje).
  // Dzieki stanowi baner nie znika gdy accounts sie pobierze i znacznik zostanie usuniety.
  const [justReturnedFromAllegro] = React.useState(() => {
    try { return localStorage.getItem('mm_return_module') === 'ratings'; } catch (e) { return false; }
  });
  React.useEffect(() => {
    if (justReturnedFromAllegro) { try { localStorage.removeItem('mm_return_module'); } catch (e) {} }
  }, [justReturnedFromAllegro]);
  const [answer, setAnswer] = useState('');
  const [removalMsg, setRemovalMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [copyToast, setCopyToast] = useState('');
  const [answerIsManual, setAnswerIsManual] = useState(false);
  const [mobileView, setMobileView] = useState<'list'|'detail'>('list');
  const isMobile = window.innerWidth < 768;
  const [ratingLang, setRatingLang] = useState('pl');
  const [answerTranslation, setAnswerTranslation] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [translatingAnswer, setTranslatingAnswer] = useState(false);
  const MAX_CHARS = 500;
  const [showRemoval, setShowRemoval] = useState(false);
  const { showSuggestions: showAnswerSuggestions, suggestions: answerSuggestions, handleChange: handleAnswerChange, applyShortcut: applyAnswerShortcut, setShowSuggestions: setShowAnswerSuggestions } = useShortcuts();
  const removalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchRatings();
    axios.get(`${API}/allegro/accounts`).then(r => { setAccounts(r.data); setAccountsLoaded(true); }).catch(() => setAccountsLoaded(true));
  }, []);

  const fetchRatings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/allegro/ratings?limit=50`);
      setRatings(res.data.ratings || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const generateAIAnswer = async (rating: Rating) => {
    setAiLoading(true);
    try {
      const msg = `Klient ${rating.buyer.login} wystawił ocenę ${rating.recommended ? 'pozytywną' : 'NEGATYWNĄ'} w sklepie ${rating.account_name}. 
Komentarz: "${rating.comment || 'brak komentarza'}"
Oceny szczegółowe: dostawa ${rating.rates?.delivery}/5, obsługa ${rating.rates?.service}/5, opis ${rating.rates?.description}/5.
Produkt: ${rating.order?.offers?.[0]?.title || 'nieznany'}.
Wygeneruj profesjonalną odpowiedź na tę ocenę. WAŻNE: odpowiedź musi mieć MAKSYMALNIE 500 znaków - twardy limit Allegro. Pisz zwięźle. Zwróć TYLKO treść odpowiedzi, bez żadnych komentarzy, liczników znaków ani dodatkowych informacji.`;
      const res = await axios.post(`${API}/ai/generate-reply`, { message: msg, account_id: rating.account_id || '', hint: answer });
      setAnswer((res.data.draft || '').replace(/---[\s\S]*$/m, '').trim());
    } catch (e) { console.error(e); }
    setAiLoading(false);
  };

  const sendAnswer = async () => {
    if (!selected || !answer.trim()) return;
    // Sprawdz polaczenie przed wyslaniem
    try {
      const accountsRes = await axios.get(`${API}/allegro/accounts`);
      const now = new Date();
      const expired = (accountsRes.data || []).filter((a: any) =>
        a.id === selected.account_id && new Date(a.expires_at + (a.expires_at.includes('Z') ? '' : 'Z')) < now
      );
      if (expired.length > 0) {
        alert(`Brak połączenia z kontem ${selected.account_name}. Odśwież połączenie w Ustawieniach.`);
        return;
      }
    } catch(e) {}
    setSending(true);
    try {
      let textToSend = answer;
      if (ratingLang !== 'pl') {
        const res = await axios.post(`${API}/ai/translate-from-polish`, { text: answer, target_lang: ratingLang });
        textToSend = res.data.translation;
      }
      await axios.put(`${API}/allegro/ratings/${selected.id}/answer`, {
        answer: textToSend,
        account_id: selected.account_id
      });
      if (answerIsManual) {
        await axios.post(`${API}/ai/approve-and-learn`, {
          question: `Ocena ${selected.recommended ? 'pozytywna' : 'negatywna'} - ${selected.comment || 'brak komentarza'}`,
          answer: answer, category: 'ocena', account_name: selected.account_name || ''
        }).catch(() => {});
      }
      setSent('Odpowiedź wysłana!');
      setAnswer('');
      setTimeout(() => { setSent(''); fetchRatings(); }, 2000);
    } catch (e: any) {
      setSent('Błąd: ' + (e.response?.data?.detail || 'nieznany'));
    }
    setSending(false);
  };

  const sendRemoval = async () => {
    if (!selected || !removalMsg.trim()) return;
    setSending(true);
    try {
      let removalToSend = removalMsg;
      if (ratingLang !== 'pl') {
        const tr = await axios.post(`${API}/ai/translate-from-polish`, { text: removalMsg, target_lang: ratingLang });
        removalToSend = tr.data.translation || removalMsg;
      }
      await axios.put(`${API}/allegro/ratings/${selected.id}/removal`, {
        message: removalToSend,
        account_id: selected.account_id
      });
      setSent('Prośba o usunięcie wysłana!');
      setRemovalMsg('');
      setShowRemoval(false);
      setTimeout(() => setSent(''), 3000);
    } catch (e: any) {
      setSent('Błąd: ' + (e.response?.data?.detail || 'nieznany'));
    }
    setSending(false);
  };

  const filtered = ratings.filter(r => {
    if (filter === 'positive' && !r.recommended) return false;
    if (filter === 'negative' && r.recommended) return false;
    if (filter === 'unanswered' && r.answer?.message) return false;
    if (filterAccount !== 'all' && r.account_id !== filterAccount) return false;
    return true;
  });

  const selectRating = async (rating: Rating) => {
    setSelected(rating);
    if (isMobile) setMobileView('detail');
    setAnswer('');
    setSent('');
    setShowRemoval(false);
    setAnswerIsManual(false);
    setAnswerTranslation('');
    // Wykryj jezyk komentarza
    const comment = rating.comment || '';
    if (comment && comment.length > 5) {
      try {
        const res = await axios.post(`${API}/ai/detect-language`, { text: comment });
        setRatingLang(res.data.language || 'pl');
      } catch(e) { setRatingLang('pl'); }
    } else {
      setRatingLang('pl');
    }
  };

  const formatDate = (d: string) => new Date(d).toLocaleString('pl-PL', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' });

  const StarRating = ({ value }: { value: number }) => (
    <span style={{ color: '#f59e0b' }}>{'★'.repeat(value)}{'☆'.repeat(5-value)}</span>
  );

  return (
    <>
      {((accountsLoaded && accounts.length === 0) || justReturnedFromAllegro) && !allegroBannerClosed && (
        <MMAllegroSetupBanner
          message="Aby odpowiadać i zarządzać ocenami od kupujących, podłącz konto Allegro."
          returnModule="ratings"
          showCongrats={justReturnedFromAllegro}
          onClose={() => setAllegroBannerClosed(true)}
        />
      )}
      {copyToast && (
        <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1d4ed8', color: '#fff', padding: '8px 18px', borderRadius: 20,
          fontSize: 13, fontWeight: 500, zIndex: 9999, pointerEvents: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
          ✓ Skopiowano: {copyToast}
        </div>
      )}
    <div style={{ display: 'flex', flexDirection: 'column' as const, height: hideHeader ? '100%' : '100dvh', fontFamily: 'system-ui, sans-serif', fontSize: 14 }}>
      {!hideHeader && <LiveAlertBar currentUser={currentUser} onOpenAiOpen={onOpenAiOpen} onOpenReturns={onOpenReturns} />}
      <div style={{ display: 'flex', flexDirection: 'row' as const, flex: 1, minHeight: 0 }}>
      {/* SIDEBAR */}
      <div style={{ width: isMobile ? '100%' : 320, display: isMobile && mobileView === 'detail' ? 'none' : 'flex', flexDirection: 'column', background: '#f9fafb', borderRight: isMobile ? 'none' : '1px solid #e5e7eb' }}>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>⭐ Oceny Allegro</span>
            <button onClick={onBack} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6,
              border: '1px solid #d1d5db', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}>
              ← Wróć
            </button>
  
          </div>
          <select value={filterAccount} onChange={e => setFilterAccount(e.target.value)}
            style={{ width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6,
              fontSize: 12, background: '#fff', marginBottom: 6 }}>
            <option value="all">Wszystkie konta</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[
              { k: 'all', l: 'Wszystkie' },
              { k: 'positive', l: '👍 Pozytywne' },
              { k: 'negative', l: '👎 Negatywne' },
              { k: 'unanswered', l: '💬 Bez odpowiedzi' },
            ].map(f => (
              <button key={f.k} onClick={() => setFilter(f.k as any)}
                style={{ fontSize: 11, padding: '3px 8px', borderRadius: 20, border: '1px solid #d1d5db',
                  background: filter === f.k ? '#1d4ed8' : 'transparent',
                  color: filter === f.k ? '#fff' : '#6b7280', cursor: 'pointer' }}>
                {f.l}
              </button>
            ))}
            <button onClick={fetchRatings} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 20,
              border: '1px solid #d1d5db', background: 'transparent', color: '#6b7280', cursor: 'pointer' }}>↻</button>
          </div>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading && <div style={{ padding: 16, color: '#6b7280', fontSize: 12 }}>Ładowanie...</div>}
          {filtered.map(r => (
            <div key={r.id} onClick={() => selectRating(r)}
              style={{ padding: '10px 14px', borderBottom: '1px solid #e5e7eb', cursor: 'pointer',
                background: selected?.id === r.id ? '#eff6ff' : '#fff',
                borderLeft: selected?.id === r.id ? '3px solid #1d4ed8' : '3px solid transparent' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ fontWeight: 500, fontSize: 12 }}>
                  {r.recommended ? '👍' : '👎'} <span title="Kliknij aby skopiować" onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(r.buyer.login); setCopyToast(r.buyer.login); setTimeout(() => setCopyToast(''), 1500); }} style={{ cursor: 'copy' }}>{r.buyer.login}</span>
                </span>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>{formatDate(r.createdAt)}</span>
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>
                {r.order?.offers?.[0]?.title?.substring(0, 45)}...
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 10, color: '#9ca3af' }}>{r.account_name}</span>
                {r.answer?.message
                  ? <span style={{ fontSize: 10, color: '#15803d' }}>✓ Odpowiedziano</span>
                  : <span style={{ fontSize: 10, color: '#dc2626' }}>● Bez odpowiedzi</span>}
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: '8px 14px', borderTop: '1px solid #e5e7eb', fontSize: 11, color: '#9ca3af' }}>
          {filtered.length} ocen · {filtered.filter(r => !r.answer?.message).length} bez odpowiedzi
        </div>
      </div>

      {/* DETAIL */}
      <div style={{ flex: 1, display: isMobile && mobileView === 'list' ? 'none' : 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {isMobile && mobileView === 'detail' && (
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
            <button onClick={() => { setMobileView('list'); setSelected(null); }}
              style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db',
                background: 'transparent', cursor: 'pointer', color: '#6b7280' }}>
              ← Wróć do listy
            </button>
          </div>
        )}
        {!selected ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>⭐</div>
              <div style={{ fontSize: 16, fontWeight: 500 }}>Wybierz ocenę</div>
            </div>
          </div>
        ) : (
          <>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>
                    {selected.recommended ? '👍 Ocena pozytywna' : '👎 Ocena negatywna'} – <span title="Kliknij aby skopiować" onClick={() => { navigator.clipboard.writeText(selected.buyer.login); setCopyToast(selected.buyer.login); setTimeout(() => setCopyToast(''), 1500); }} style={{ cursor: 'copy' }}>{selected.buyer.login}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {formatDate(selected.createdAt)} · {selected.account_name}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {!selected.recommended && selected.removal?.possibleTo && (
                    <button onClick={() => { setShowRemoval(true); setTimeout(() => { removalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100); }}
                      style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6,
                        border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}>
                      Prośba o usunięcie
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', background: '#f9fafb' }}>
              {/* Ocena szczegółowa */}
              <div style={{ background: '#fff', borderRadius: 8, padding: 14, border: '1px solid #e5e7eb', marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 10 }}>Oceny szczegółowe</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    { label: 'Dostawa', val: selected.rates?.delivery },
                    { label: 'Obsługa', val: selected.rates?.service },
                    { label: 'Opis', val: selected.rates?.description },
                    { label: 'Koszt dostawy', val: selected.rates?.deliveryCost },
                  ].map(r => (
                    <div key={r.label} style={{ fontSize: 12 }}>
                      <span style={{ color: '#6b7280' }}>{r.label}: </span>
                      <StarRating value={r.val || 0} />
                    </div>
                  ))}
                </div>
                {selected.order?.offers?.[0] && (
                  <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
                    Produkt: <span style={{ color: '#374151' }}>{selected.order.offers[0].title}</span>
                  </div>
                )}
              </div>

              {/* Komentarz klienta */}
              {selected.comment && (
                <div style={{ background: '#fff', borderRadius: 8, padding: 14, border: '1px solid #e5e7eb', marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Komentarz klienta</div>
                  <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{selected.comment}</div>
                </div>
              )}

              {/* Istniejaca odpowiedz */}
              {selected.answer?.message && (
                <div style={{ background: '#f0fdf4', borderRadius: 8, padding: 14, border: '1px solid #bbf7d0', marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#15803d', marginBottom: 6 }}>
                    ✓ Twoja odpowiedź ({formatDate(selected.answer.createdAt)})
                  </div>
                  <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{selected.answer.message}</div>
                </div>
              )}

              {/* Prosba o usuniecie */}
              {showRemoval && (
                <div ref={removalRef} style={{ background: '#fef2f2', borderRadius: 8, padding: 14, border: '1px solid #fca5a5', marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#dc2626', marginBottom: 8 }}>
                    Prośba o usunięcie oceny
                    <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 8, color: '#9ca3af' }}>
                      (możliwe do: {new Date(selected.removal!.possibleTo).toLocaleDateString('pl-PL')})
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: '#7B79C9', marginBottom: 6, padding: '4px 8px', background: '#EEEDFE', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span>🌐 Klient otrzyma prośbę w języku:</span>
                    <select value={ratingLang} onChange={e => setRatingLang(e.target.value)} style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #C4C2F5', borderRadius: 6, color: '#3C3489', background: '#fff', cursor: 'pointer' }}>
                      {[{v:'pl',l:'🇵🇱 Polski'},{v:'en',l:'🇬🇧 Angielski'},{v:'de',l:'🇩🇪 Niemiecki'},{v:'cs',l:'🇨🇿 Czeski'},{v:'sk',l:'🇸🇰 Słowacki'},{v:'hu',l:'🇭🇺 Węgierski'},{v:'ro',l:'🇷🇴 Rumuński'},{v:'uk',l:'🇺🇦 Ukraiński'},{v:'ru',l:'🇷🇺 Rosyjski'},{v:'fr',l:'🇫🇷 Francuski'},{v:'it',l:'🇮🇹 Włoski'}].map((o: any) => (
                        <option key={o.v} value={o.v}>{o.l}</option>
                      ))}
                    </select>
                  </div>
                  <textarea value={removalMsg} onChange={e => { setRemovalMsg(e.target.value.slice(0, MAX_CHARS)); }} onInput={(e:any)=>{const t=e.currentTarget;t.style.height='auto';t.style.height=Math.min(t.scrollHeight,200)+'px';}}
                    placeholder="Wyjaśnij dlaczego ocena powinna zostać usunięta..."
                    style={{ width: '100%', minHeight: 80, padding: '8px 12px', border: '1px solid #fca5a5',
                      borderRadius: 6, fontSize: 13, fontFamily: 'system-ui', resize: 'vertical',
                      outline: 'none', boxSizing: 'border-box', marginBottom: 8 }} />
                  <div style={{ fontSize: 11, color: removalMsg.length > MAX_CHARS * 0.9 ? '#dc2626' : '#9ca3af', textAlign: 'right', marginBottom: 6 }}>
                    {removalMsg.length}/{MAX_CHARS} znaków
                  </div>
                  <button onClick={sendRemoval} disabled={sending || !removalMsg.trim()}
                    style={{ padding: '7px 14px', background: '#dc2626', color: '#fff', border: 'none',
                      borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                    Wyślij prośbę
                  </button>
                </div>
              )}
            </div>

            {/* Odpowiedz na ocene */}
            <div style={{ borderTop: '1px solid #e5e7eb', padding: '14px 18px', background: '#fff' }}>
              {sent && (
                <div style={{ padding: '7px 12px', borderRadius: 6, fontSize: 12, marginBottom: 8,
                  background: sent.includes('Błąd') ? '#fef2f2' : '#f0fdf4',
                  color: sent.includes('Błąd') ? '#dc2626' : '#15803d' }}>
                  {sent}
                </div>
              )}
              <div style={{ fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
                <span>{selected.answer?.message ? 'Zmień odpowiedź na ocenę' : 'Odpowiedz na ocenę'}</span>
              </div>
              <div style={{ fontSize: 11, color: '#7B79C9', marginBottom: 6, padding: '4px 8px', background: '#EEEDFE', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span>🌐 Klient otrzyma odpowiedź w języku:</span>
                <select value={ratingLang} onChange={e => { setRatingLang(e.target.value); setAnswerTranslation(''); }} style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #C4C2F5', borderRadius: 6, color: '#3C3489', background: '#fff', cursor: 'pointer' }}>
                  {[{v:'pl',l:'🇵🇱 Polski'},{v:'en',l:'🇬🇧 Angielski'},{v:'de',l:'🇩🇪 Niemiecki'},{v:'cs',l:'🇨🇿 Czeski'},{v:'sk',l:'🇸🇰 Słowacki'},{v:'hu',l:'🇭🇺 Węgierski'},{v:'ro',l:'🇷🇴 Rumuński'},{v:'uk',l:'🇺🇦 Ukraiński'},{v:'ru',l:'🇷🇺 Rosyjski'},{v:'fr',l:'🇫🇷 Francuski'},{v:'it',l:'🇮🇹 Włoski'}].map((o: any) => (
                    <option key={o.v} value={o.v}>{o.l}</option>
                  ))}
                </select>
              </div>
              {answerTranslation && (
                <div style={{ padding: '8px 10px', background: '#EEEDFE', borderRadius: 6, fontSize: 12, color: '#3C3489', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, marginBottom: 3 }}>Podgląd {ratingLang.toUpperCase()}:</div>
                  {answerTranslation}
                </div>
              )}
              <div style={{ position: 'relative' }}>
              <textarea value={answer} onChange={e => { const v = e.target.value.slice(0, MAX_CHARS); handleAnswerChange(v, (nv) => { setAnswer(nv); setAnswerTranslation(''); setAnswerIsManual(true); }); }} onKeyDown={e => { if (e.key === 'Escape') setShowAnswerSuggestions(false); }} onInput={(e:any)=>{const t=e.currentTarget;t.style.height='auto';t.style.height=Math.min(t.scrollHeight,200)+'px';}}
                placeholder={ratingLang !== 'pl' ? ('Wpisz odpowiedź (dowolny język) – klient otrzyma po ' + ratingLang.toUpperCase()) : 'Wpisz odpowiedź na ocenę...'}
                style={{ width: '100%', minHeight: isMobile ? 80 : 160, padding: '9px 12px', border: `1px solid ${answer.length > MAX_CHARS * 0.9 ? '#fca5a5' : '#d1d5db'}`,
                  borderRadius: 6, fontSize: 13, fontFamily: 'system-ui', resize: 'vertical',
                  outline: 'none', boxSizing: 'border-box' }} />
              {showAnswerSuggestions && <ShortcutSuggestions suggestions={answerSuggestions} onSelect={s => applyAnswerShortcut(s, answer, (v) => { setAnswer(v); setAnswerTranslation(''); setAnswerIsManual(true); })} onClose={() => setShowAnswerSuggestions(false)} />}
              </div>
              <div style={{ fontSize: 11, color: answer.length > MAX_CHARS * 0.9 ? '#dc2626' : '#9ca3af', textAlign: 'right', marginTop: 2 }}>
                {answer.length}/{MAX_CHARS} znaków
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                {selected && isAccountExpired(selected.account_name || '') && (
                  <div style={{ padding: '6px 10px', background: '#fef2f2', borderRadius: 6, fontSize: 12,
                    color: '#dc2626', marginBottom: 8 }}>
                    ⚠️ Brak połączenia z {selected.account_name}
                  </div>
                )}
                <button onClick={sendAnswer} disabled={sending || !answer.trim() || isAccountExpired(selected?.account_name || '')}
                  style={{ padding: '7px 16px', background: !answer.trim() ? '#9ca3af' : '#1d4ed8',
                    color: '#fff', border: 'none', borderRadius: 6, fontSize: 13,
                    cursor: !answer.trim() ? 'not-allowed' : 'pointer', fontWeight: 500 }}>
                  Wyślij odpowiedź
                </button>
                <button onClick={() => generateAIAnswer(selected)} disabled={aiLoading}
                  style={{ padding: '7px 14px', background: '#f0fdf4', color: '#15803d',
                    border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  {aiLoading ? '⏳ Generuję...' : '✦ Generuj AI'}
                </button>
                <button onClick={() => setAnswer('')}
                  style={{ padding: '7px 14px', background: 'transparent', color: '#6b7280',
                    border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  Wyczyść
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {window.innerWidth < 768 && !hideHeader && (
        <div style={{ display: 'flex', background: '#1d4ed8', padding: '8px 0', paddingBottom: 'env(safe-area-inset-bottom)', flexShrink: 0 }}>
          <button onClick={onBack}
            style={{ flex: 1, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', cursor: 'pointer', padding: '6px', fontSize: 11, borderRadius: 8, margin: '0 8px' }}>
            <div style={{ fontSize: 20 }}>←</div>
            <div>Wróć</div>
          </button>
        </div>
      )}
    </div>
    </div>
    </>
  );
}